// src/pages/AdminSkillsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  SelectChangeEvent
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { 
  getSkills, 
  createSkill, 
  updateSkill, 
  deleteSkill, 
  Skill 
} from '../api/skillService';
import './AdminSkillsPage.css'; // Import the CSS file

const AdminSkillsPage: React.FC = () => {
  const { user } = useAuth();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [currentSkill, setCurrentSkill] = useState<Partial<Skill>>({});
  const [isEditing, setIsEditing] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<number | null>(null);

  // Categories for dropdown
  const categories = ["Programming", "Languages", "Frameworks", "Databases", "Tools", "Soft Skills", "Other"];

  // Fetch skills
  const fetchSkills = async () => {
    try {
      setLoading(true);
      const data = await getSkills();
      setSkills(data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching skills:', err);
      setError(`Failed to load skills: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  // Handle dialog open for create/edit
  const handleOpenDialog = (skill?: Skill) => {
    if (skill) {
      setCurrentSkill(skill);
      setIsEditing(true);
    } else {
      setCurrentSkill({
        name: '',
        category: '',
        proficiency: 50,
        description: ''
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentSkill({});
  };

  // Handle form input change for text fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentSkill({
      ...currentSkill,
      [name]: value
    });
  };

  // Handle select change separately
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setCurrentSkill({
      ...currentSkill,
      [name as string]: value
    });
  };

  // Handle slider change for proficiency
  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setCurrentSkill({
      ...currentSkill,
      proficiency: newValue as number
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (isEditing && currentSkill.id) {
        await updateSkill(currentSkill.id, currentSkill);
        setSuccess('Skill updated successfully');
      } else {
        await createSkill(currentSkill as Omit<Skill, 'id'>);
        setSuccess('Skill created successfully');
      }
      handleCloseDialog();
      fetchSkills();
    } catch (err: any) {
      setError(`Failed to save skill: ${err.message}`);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    setSkillToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (skillToDelete) {
      try {
        await deleteSkill(skillToDelete);
        setSuccess('Skill deleted successfully');
        fetchSkills();
      } catch (err: any) {
        setError(`Failed to delete skill: ${err.message}`);
      }
    }
    setDeleteDialogOpen(false);
    setSkillToDelete(null);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Check if user can edit (admin or editor)
  const canEdit = user && ['admin', 'editor'].includes(user.role);
  
  // Check if user can delete (admin only)
  const canDelete = user && user.role === 'admin';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Skills
          </Typography>
          
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Skill
            </Button>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} className="success-message">
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} className="error-message">
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell className="proficiency-slider-cell">Proficiency</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.map((skill) => (
                  <TableRow key={skill.id}>
                    <TableCell>{skill.name}</TableCell>
                    <TableCell>
                      <span className="category-badge">{skill.category}</span>
                    </TableCell>
                    <TableCell className="proficiency-slider-cell">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100px', mr: 1 }}>
                          <Slider
                            value={skill.proficiency}
                            disabled
                            aria-label="Proficiency"
                          />
                        </Box>
                        {skill.proficiency}%
                      </Box>
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <IconButton onClick={() => handleOpenDialog(skill)} color="primary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton onClick={() => handleDelete(skill.id!)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {skills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No skills found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="Name"
            name="name"
            fullWidth
            value={currentSkill.name || ''}
            onChange={handleInputChange}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              name="category"
              value={currentSkill.category || ''}
              label="Category"
              onChange={handleSelectChange}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography id="proficiency-slider" gutterBottom>
              Proficiency: {currentSkill.proficiency || 0}%
            </Typography>
            <Slider
              value={currentSkill.proficiency || 0}
              onChange={handleSliderChange}
              aria-labelledby="proficiency-slider"
              valueLabelDisplay="auto"
              step={5}
              marks
              min={0}
              max={100}
            />
          </Box>
          
          <TextField
            margin="normal"
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={4}
            value={currentSkill.description || ''}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this skill? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminSkillsPage;
